import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AnimatePresence } from 'framer-motion';
import { BellIcon } from '@heroicons/react/24/outline';
import {
  useNotifications,
  useUnreadCount,
  useMarkAsRead,
  useMarkAllAsRead,
} from '../../hooks/api/useNotifications';
import NotificationDropdown, {
  NotificationFilter,
} from './NotificationDropdown';
import { Notification } from '../../types/api';

export interface NotificationBellProps {
  /** Path to the full notifications page. Defaults to `/notifications`. */
  viewAllPath?: string;
}

const NotificationBell: React.FC<NotificationBellProps> = ({
  viewAllPath = '/notifications',
}) => {
  const [open, setOpen] = useState(false);
  const [filter, setFilter] = useState<NotificationFilter>('all');
  const containerRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();

  const { data: unread } = useUnreadCount();
  const count = unread?.count ?? 0;
  const badge = count > 99 ? '99+' : String(count);

  // Only fetch the list when dropdown is open to avoid unnecessary calls
  const { data, isLoading } = useNotifications(
    open ? { limit: 10 } : undefined
  );
  const markAsRead = useMarkAsRead();
  const markAllAsRead = useMarkAllAsRead();

  useEffect(() => {
    if (!open) return;
    const handleClick = (event: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target as Node)
      ) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [open]);

  const handleItemClick = (n: Notification) => {
    if (!n.is_read) {
      markAsRead.mutate(n.id);
    }
    setOpen(false);
    if (n.link) {
      navigate(n.link);
    }
  };

  const handleMarkAllRead = () => {
    markAllAsRead.mutate();
  };

  return (
    <div className="relative" ref={containerRef}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-label="Notifications"
        aria-haspopup="true"
        aria-expanded={open}
        className="relative p-2 text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-gray-100 hover:bg-gray-100 dark:hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-primary-500 rounded-md transition-colors duration-200"
      >
        <BellIcon className="w-6 h-6" />
        {count > 0 && (
          <span
            data-testid="notification-badge"
            className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 inline-flex items-center justify-center rounded-full bg-red-500 text-white text-[10px] font-semibold ring-2 ring-white dark:ring-gray-800"
          >
            {badge}
          </span>
        )}
      </button>

      <AnimatePresence>
        {open && (
          <NotificationDropdown
            notifications={data?.items ?? []}
            isLoading={isLoading}
            filter={filter}
            onFilterChange={setFilter}
            onItemClick={handleItemClick}
            onMarkAllRead={handleMarkAllRead}
            viewAllPath={viewAllPath}
            onViewAll={() => setOpen(false)}
          />
        )}
      </AnimatePresence>
    </div>
  );
};

export default NotificationBell;
